<template>
	<v-card tile elevation="0" style="height: 100%">
		<v-card-title class="card-header text-left fixed-card-title2">
			<v-row no-gutters>
				<v-col class="align-center d-flex" cols="10">
					<v-toolbar-title>{{ $t('edit-schedule') }}</v-toolbar-title>
				</v-col>
				<v-spacer />
				<v-icon color="white" @click="closeDrawer">mdi-close</v-icon>
			</v-row>
		</v-card-title>

		<v-card-text class="overflow-y-auto pt-4" style="height: calc(100vh - 158px)" v-if="!loading">
			<v-form ref="scheduleForm" v-model="formValid" lazy-validation>
				<v-row dense>
					<v-col cols="12">
						<!-- Schedule info -->
						<v-expansion-panels v-model="expandedScheduleInfo">
							<v-expansion-panel>
								<v-expansion-panel-header class="pa-4">
									<template #actions>
										<v-icon color="primary">mdi-menu-down</v-icon>
									</template>
									{{ $t('schedule-info') }}
								</v-expansion-panel-header>
								<v-expansion-panel-content class="low-padding-expansion-panel pa-0">
									<v-row dense>
										<!-- Locations/Hospitals -->
										<v-col cols="12" class="text-left">
											<span>{{ $t('select-locations') }}</span>
											<v-data-table
												v-if="hospitals?.length > 0 && selectedScheduleToEdit"
												:headers="headersHospitals"
												height="300"
												:items="hospitals"
												show-select
												dense
												v-model="selectedScheduleToEdit.scheduleLocations"
												:items-per-page="-1"
												hide-default-footer
												fixed-header
												class="rounded-lg elevation-2">
												<template #[`header.data-table-select`]>
													<v-simple-checkbox
														:indeterminate="
															selectedScheduleToEdit.scheduleLocations.length > 0 &&
															selectedScheduleToEdit.scheduleLocations.length < hospitals.length
														"
														:value="selectedScheduleToEdit.scheduleLocations.length === hospitals.length"
														@click="
															selectedScheduleToEdit.scheduleLocations =
																selectedScheduleToEdit.scheduleLocations.length === hospitals.length
																	? []
																	: hospitals.slice()
														"
														color="primary" />
												</template>
												<template #[`item.data-table-select`]="{ select, isSelected }">
													<v-simple-checkbox :value="isSelected" @click="select(!isSelected)" color="primary" />
												</template>
												<template #[`item.hospitalName`]="{ item }">
													{{ item.hospitalName }}
												</template>
											</v-data-table>
											<span
												class="color-primary d-flex px-3 font-size12"
												v-if="
													selectedScheduleToEdit.scheduleLocations &&
													selectedScheduleToEdit.scheduleLocations.length === 0
												">
												{{ $t('at-least-one-location-should-be-selected') }}
											</span>
										</v-col>
										<!-- Schedule Owner -->
										<v-col class="d-flex flex-column text-left" cols="12">
											<span>{{ $t('select-staff') }}</span>
											<v-data-table
												:headers="headersOwnersAndStaff"
												:items="medicalStaffAbleToDoBillingForOrg"
												height="300"
												show-select
												outlined
												dense
												v-model="selectedScheduleToEdit.scheduleExecutors"
												:items-per-page="-1"
												hide-default-footer
												fixed-header
												class="rounded-lg elevation-2">
												<template #[`header.data-table-select`]>
													<v-simple-checkbox
														:indeterminate="
															selectedScheduleToEdit.scheduleExecutors.length > 0 &&
															selectedScheduleToEdit.scheduleExecutors.length < medicalStaffAbleToDoBillingForOrg.length
														"
														:value="
															selectedScheduleToEdit.scheduleExecutors.length ===
															medicalStaffAbleToDoBillingForOrg.length
														"
														@click="
															selectedScheduleToEdit.scheduleExecutors =
																selectedScheduleToEdit.scheduleExecutors.length ===
																medicalStaffAbleToDoBillingForOrg.length
																	? []
																	: medicalStaffAbleToDoBillingForOrg.slice()
														"
														color="primary" />
												</template>
												<template #[`item.data-table-select`]="{ select, isSelected }">
													<v-simple-checkbox :value="isSelected" @click="select(!isSelected)" color="primary" />
												</template>
												<template #[`item.firstName`]="{ item }"> {{ item.firstName }} {{ item.lastName }} </template>
											</v-data-table>
										</v-col>
										<!-- Schedule rrule name -->
										<v-col cols="12">
											<v-text-field
												type="text"
												v-model="selectedScheduleToEdit.scheduleRuleName"
												outlined
												dense
												:label="$t('name')"
												clearable
												hide-details="auto"
												:rules="[(v) => !!v || $t('required-field')]"
												validate-on-blur />
										</v-col>
										<!-- Schedule rrule description -->
										<v-col cols="12">
											<v-textarea
												type="text"
												v-model="selectedScheduleToEdit.scheduleRuleDescription"
												outlined
												hide-details
												dense
												:label="$t('description')"
												rows="3"
												clearable />
										</v-col>
									</v-row>
								</v-expansion-panel-content>
							</v-expansion-panel>
							<v-expansion-panel
								v-for="(tasksOccurrence, indexSchedule) in selectedScheduleToEdit.scheduleOccurrences"
								:key="indexSchedule">
								<v-expansion-panel-header class="pa-4">
									<template #actions>
										<v-icon color="primary">mdi-menu-down</v-icon>
									</template>
									<div v-if="selectedScheduleToEdit.scheduleOccurrences.length > 1">
										<Tooltip
											position="top"
											v-if="hasOccurrenceAnyTask(tasksOccurrence.id)"
											:text="$t('task-list-cant-be-deleted-because-has-tasks')">
											<v-icon color="primary" :disabled="hasOccurrenceAnyTask(tasksOccurrence.id)">mdi-delete</v-icon>
										</Tooltip>
										<v-icon
											color="primary"
											v-else
											@click.stop="deleteTaskOccurrence(indexSchedule)"
											:disabled="hasOccurrenceAnyTask(tasksOccurrence.id)">
											mdi-delete
										</v-icon>
										{{ $t('task-list') }}: {{ tasksOccurrence.occurrenceName }}
									</div>
									<div v-else>{{ $t('task-list') }}: {{ tasksOccurrence.occurrenceName }}</div>
								</v-expansion-panel-header>
								<v-expansion-panel-content class="low-padding-expansion-panel">
									<v-row dense>
										<!-- Occurrence rrule name -->
										<v-col cols="12">
											<v-text-field
												type="text"
												v-model="tasksOccurrence.occurrenceName"
												outlined
												dense
												:label="$t('name')"
												clearable
												hide-details="auto"
												:rules="[(v) => !!v || $t('required-field')]"
												validate-on-blur />
										</v-col>
										<!-- Occurrence rrule description -->
										<v-col cols="12">
											<v-textarea
												type="text"
												v-model="tasksOccurrence.occurrenceDescription"
												outlined
												dense
												hide-details
												:label="$t('description')"
												rows="3"
												clearable />
										</v-col>
										<!-- Occurrence rrule frequency -->
										<v-col class="ma-0 pr-0 d-flex flex-row align-baseline" cols="4">
											<v-select
												attach
												class="pr-1"
												v-model="tasksOccurrence.frequency"
												:items="freqOptions"
												:label="$t('frequency')"
												outlined
												hide-details
												dense
												:menu-props="{ offsetY: true }"
												:disabled="tasksOccurrence.byWeekDay.length + tasksOccurrence.byMonthDay.length > 0" />
											<Tooltip :text="$t('rrule-explanation-frequency')">
												<v-icon color="grey">mdi-help-circle-outline</v-icon>
											</Tooltip>
										</v-col>
										<!-- Occurrence rrule interval -->
										<v-col class="ma-0 pr-0 d-flex flex-row align-baseline" cols="4">
											<v-text-field
												class="pr-1"
												v-model.number="tasksOccurrence.interval"
												type="number"
												:label="$t('interval')"
												outlined
												hide-details
												dense
												@keypress="(e) => /[0-9]/.test(e.key) || e.preventDefault()"
												min="1" />
											<Tooltip :text="$t('rrule-explanation-interval')">
												<v-icon color="grey">mdi-help-circle-outline</v-icon>
											</Tooltip>
										</v-col>
										<!-- Occurrence rrule count -->
										<v-col class="ma-0 d-flex flex-row align-baseline" cols="4">
											<v-text-field
												class="pr-1"
												v-model.number="tasksOccurrence.count"
												type="number"
												:label="$t('count')"
												outlined
												@keypress="(e) => /[0-9]/.test(e.key) || e.preventDefault()"
												dense
												hide-details="auto" />
											<Tooltip :text="$t('rrule-explanation-count')">
												<v-icon color="grey">mdi-help-circle-outline</v-icon>
											</Tooltip>
										</v-col>

										<!-- DEFAULT / FALLBACK TIME -->
										<v-col cols="4" class="pt-2">
											<v-text-field
												v-model="tasksOccurrence.defaultTime"
												type="time"
												:label="$t('start-time')"
												outlined
												dense
												hide-details
												clearable
												:disabled="tasksOccurrence.byHour.length > 0 || tasksOccurrence.byMinute.length > 0"
												placeholder="HH:MM" />
										</v-col>

										<!-- Task Duration -->
										<v-col class="ma-0 d-flex flex-row align-center pt-2" cols="8">
											<v-text-field
												:disabled="isAllDayTask"
												v-model.number="tasksOccurrence.occurrenceDuration"
												class="pr-1"
												type="number"
												:label="$t('planned-duration')"
												@keypress="(e) => /[0-9]/.test(e.key) || e.preventDefault()"
												outlined
												dense
												hide-details="auto"
												:rules="[
													(v) => v > 0 || $t('value-should-be-greater-than-zero'),
													(v) => v <= 1439 || $t('value-should-be-less-than-1440'),
												]"
												validate-on-blur
												min="1"
												max="1439" />
											<Tooltip :text="$t('rrule-explanation-duration-minutes')">
												<v-icon size="20" color="grey">mdi-help-circle-outline</v-icon>
											</Tooltip>
											<v-switch
												color="primary"
												:label="$t('all-day') + '?'"
												:value="tasksOccurrence.occurrenceDuration >= 1439"
												@change="onIsAllDayTaskChange($event, indexSchedule)"
												hide-details
												class="pl-2 ma-0" />
										</v-col>
										<!-- Expandable Advanced Options -->
										<v-col cols="12">
											<v-expansion-panels v-model="expandedAdvancedOptions" style="height: calc(100% - 56px)" elevation>
												<v-expansion-panel class="ma-0">
													<v-expansion-panel-header class="pa-2">
														<template #actions>
															<v-icon color="primary">mdi-menu-down</v-icon>
														</template>
														{{ $t('advanced-options') }}
													</v-expansion-panel-header>
													<v-expansion-panel-content class="low-padding-expansion-panel pa-0">
														<v-row dense>
															<!-- BY WEEKDAY -->
															<v-col class="ma-0 d-flex flex-row align-baseline" cols="12">
																<v-select
																	attach
																	v-model="tasksOccurrence.byWeekDay"
																	:items="weekdayOptions"
																	:label="$t('by-weekday')"
																	multiple
																	outlined
																	hide-details
																	dense
																	clearable
																	:menu-props="{ offsetY: true }">
																	<template #selection="{ item, index }">
																		<span v-if="index === 0">
																			{{ item.text }}
																		</span>
																		<span
																			v-if="index === 1 && tasksOccurrence.byWeekDay.length > 1"
																			class="grey--text text-caption">
																			&nbsp;(+{{ tasksOccurrence.byWeekDay.length - 1 }} {{ $t('more') }})
																		</span>
																	</template>
																</v-select>
																<Tooltip :text="$t('rrule-explanation-by-day')">
																	<v-icon color="grey">mdi-help-circle-outline</v-icon>
																</Tooltip>
															</v-col>
															<!-- BY MONTH DAY -->
															<v-col class="ma-0 d-flex flex-row align-baseline" cols="12">
																<v-select
																	attach
																	v-model="tasksOccurrence.byMonthDay"
																	:items="monthDayOptions"
																	:label="$t('by-date')"
																	multiple
																	outlined
																	hide-details
																	dense
																	clearable
																	:menu-props="{ offsetY: true }">
																	<template #selection="{ item, index }">
																		<v-chip v-if="index === 0" small>
																			{{ item.text }}
																		</v-chip>
																		<span
																			v-if="index === 1 && tasksOccurrence.byMonthDay.length > 1"
																			class="grey--text text-caption">
																			&nbsp;(+{{ tasksOccurrence.byMonthDay.length - 1 }} {{ $t('more') }})
																		</span>
																	</template>
																</v-select>
																<Tooltip :text="$t('rrule-explanation-by-date')">
																	<v-icon color="grey">mdi-help-circle-outline</v-icon>
																</Tooltip>
															</v-col>
															<!-- BY HOUR -->
															<v-col class="ma-0 d-flex flex-row align-baseline" cols="12">
																<v-select
																	attach
																	v-model="tasksOccurrence.byHour"
																	:items="hourOptions"
																	:label="$t('by-hour')"
																	multiple
																	outlined
																	hide-details
																	dense
																	clearable
																	:menu-props="{ offsetY: true }">
																	<template #selection="{ item, index }">
																		<v-chip v-if="index === 0" small>
																			{{ item.text }}
																		</v-chip>
																		<span
																			v-if="index === 1 && tasksOccurrence.byHour.length > 1"
																			class="grey--text text-caption">
																			&nbsp;(+{{ tasksOccurrence.byHour.length - 1 }} {{ $t('more') }})
																		</span>
																	</template>
																</v-select>
																<Tooltip :text="$t('rrule-explanation-by-hour')">
																	<v-icon color="grey">mdi-help-circle-outline</v-icon>
																</Tooltip>
															</v-col>
															<!-- BY MINUTE -->
															<v-col class="ma-0 d-flex flex-row align-baseline" cols="12">
																<v-select
																	attach
																	v-model="tasksOccurrence.byMinute"
																	:items="minuteOptions"
																	:label="$t('by-minute')"
																	multiple
																	outlined
																	hide-details
																	dense
																	clearable
																	:menu-props="{ offsetY: true }">
																	<template #selection="{ item, index }">
																		<v-chip v-if="index === 0" small>
																			{{ item.text }}
																		</v-chip>
																		<span
																			v-if="index === 1 && tasksOccurrence.byMinute.length > 1"
																			class="grey--text text-caption">
																			&nbsp;(+{{ tasksOccurrence.byMinute.length - 1 }} {{ $t('more') }})
																		</span>
																	</template>
																</v-select>
																<Tooltip :text="$t('rrule-explanation-by-minute')">
																	<v-icon color="grey">mdi-help-circle-outline</v-icon>
																</Tooltip>
															</v-col>

															<!-- <v-col cols="12">
																<FileUpload
																	@file-selected="onFileSelected"
																	@uploaded="onFileUploaded"
																	@error="onFileError" />
															</v-col> -->
														</v-row>
													</v-expansion-panel-content>
												</v-expansion-panel>
											</v-expansion-panels>
										</v-col>
									</v-row>
								</v-expansion-panel-content>
							</v-expansion-panel>
						</v-expansion-panels>
					</v-col>
					<v-col cols="12">
						<v-btn block outlined color="primary" @click="addNewRRule()">+ {{ $t('add-new-task-list-item') }}</v-btn>
					</v-col>
				</v-row>
			</v-form>
		</v-card-text>
		<v-container class="px-4" v-if="!loading">
			<v-row dense>
				<v-col cols="12" order="1">
					<v-btn block small outlined color="primary" :disabled="!canSaveRRule" @click="previewSchedule">
						{{ $t('click-to-preview-events') }}
					</v-btn>
				</v-col>
				<v-col cols="12" order="2">
					<v-btn block color="primary" :disabled="!canSaveRRule" @click="saveRRule">{{ $t('save') }}</v-btn>
				</v-col>
			</v-row>
		</v-container>
		<v-dialog v-if="showDialog" persistent v-model="showDialog" min-width="600px" width="50vw">
			<v-card class="pa-2">
				<v-card-title class="text-capitalize">
					{{ $t('preview') }} {{ $t('events') }}
					<v-spacer />
					<v-icon color="black" @click="showDialog = false">mdi-close</v-icon>
				</v-card-title>
				<v-card-text>
					<div class="d-flex flex-row flex-wrap justify-space-between align-center">
						<div class="d-flex">
							<v-btn
								elevation="0"
								tile
								@click="calendarView = 'month'"
								class="white--text"
								:color="calendarView === 'month' ? 'primary' : 'primary2'"
								style="border-radius: 8px 0 0 8px">
								{{ $t('month') }}
							</v-btn>
							<v-btn
								elevation="0"
								tile
								@click="calendarView = 'week'"
								class="white--text"
								:class="calendarView === 'week' ? 'primaryBg' : 'primary2Bg'"
								style="border-left: 1px solid #000; border-right: 1px solid #000">
								{{ $t('week') }}
							</v-btn>
							<v-btn
								elevation="0"
								tile
								@click="calendarView = 'day'"
								:color="calendarView === 'day' ? 'primary' : 'primary2'"
								class="white--text"
								style="border-radius: 0 8px 8px 0">
								{{ $t('day') }}
							</v-btn>
						</div>
						<h3>{{ getMonthName(currentMonth) }} {{ currentYear }}</h3>
						<div class="my-2">
							<v-btn color="primary" @click="goPrev"><v-icon>mdi-arrow-left</v-icon>{{ $t('prev') }}</v-btn>
							<v-btn color="primary" @click="goToday" class="white--text">
								{{ $t('today') }}
							</v-btn>
							<v-btn color="primary" @click="goNext">{{ $t('next') }}<v-icon>mdi-arrow-right</v-icon></v-btn>
						</div>
					</div>
					<v-sheet height="600">
						<v-calendar
							ref="calendar"
							v-model="currentDate"
							:type="calendarView"
							:events="calendarEvents"
							:event-color="(e) => e.color"
							@click:more="viewDate"
							@click:date="viewDate"
							:weekdays="[1, 2, 3, 4, 5, 6, 0]">
							<template #event="{ event }">
								<v-tooltip
									v-if="event.description"
									:color="!event.isSuccess && !event.isError ? 'light' : 'primary'"
									top>
									<template #activator="{ on }">
										<div
											class="d-flex align-center justify-center black--text overflow-hidden"
											style="height: 100%"
											v-on="on">
											{{ event.name }}
											<span v-if="event.isSuccess">
												<v-icon class="outlined-icon" color="success" size="small"> mdi-check-circle </v-icon>
											</span>
											<span v-else-if="event.isError">
												<v-icon class="outlined-icon" color="primary" size="small"> mdi-close-circle </v-icon>
											</span>
										</div>
									</template>
									<span>{{ event.description }}</span>
								</v-tooltip>
								<div v-else class="d-flex align-center justify-center black--text overflow-hidden" style="height: 100%">
									{{ event.name }}
									<span v-if="event.isSuccess">
										<v-icon class="outlined-icon" color="success" size="small"> mdi-check-circle </v-icon>
									</span>
									<span v-else-if="event.isError">
										<v-icon class="outlined-icon" color="primary" size="small"> mdi-close-circle </v-icon>
									</span>
								</div>
							</template>

							<template #day-label-header="{ day, date }">
								<div class="text-center">
									<div>{{ day }} {{ monthName(date) }}</div>
								</div>
							</template>
						</v-calendar>
					</v-sheet>
				</v-card-text>
			</v-card>
		</v-dialog>
		<div v-if="loading" class="d-flex flex-column align-center justify-center" style="height: calc(100vh - 62px)">
			<!-- Spinner -->
			<v-progress-circular indeterminate color="primary" size="64" width="6" />
			<!-- Loading Text -->
			<span class="mt-4 text-h6">{{ $t('loading') }}</span>
		</div>
	</v-card>
</template>

<script>
import { mapActions, mapState } from 'vuex';
import { RRule, RRuleSet } from 'rrule';
// import FileUpload from './FileUpload.vue';
import {
	getHospitals,
	getMonthName,
	monthName,
	months,
	showErrorAlert,
	showSuccessAlert,
} from '../../utils/utilities';
import Tooltip from '../Client/components/Tooltip.vue';

export default {
	components: { Tooltip },
	data () {
		return {
			loading: true,
			expandedAdvancedOptions: [],
			rruleNew: {
				occurrenceName: '',
				occurrenceDescription: '',
				occurrenceStartAt: '', // rrule.dtstart
				occurrenceEndAt: null, // rrule.until
				rruleString: '',
				occurrenceDuration: 60,
				frequency: RRule.DAILY, // rrule.freq
				interval: null, // rrule.interval
				count: null, // rrule.count
				byWeekDay: [], // rrule.byWeekDay
				startDayOfWeek: RRule.MO.weekday, // rrule.wkst
				byMonth: [], // rrule.bymonth
				byMonthDay: [], // rrule.bymonthday
				byYearDay: [], // rrule.byyearday
				byWeekNumber: [], // rrule.byweekno
				byHour: [], // rrule.byhour
				byMinute: [], // rrule.byminute
				defaultTime: '00:00', // fallback time string 'HH:MM' used when byHour/byMinute not set
				status: 0,
			},
			calendarEvents: [],
			hourOptions: Array.from({ length: 24 }, (_, i) => ({ text: `${i}:00`, value: i })),
			minuteOptions: Array.from({ length: 12 }, (_, i) => ({ text: `${i * 5}`, value: i * 5 })),
			selectedStartDateTime: '',
			isValidSelectedStartDateTime: false,
			selectedEndDateTime: '',
			isValidSelectedEndDateTime: false,

			calendarView: 'month',
			currentDate: new Date().toISOString().substring(0, 10),
			medicalStaffAbleToDoBillingForOrg: [],
			expandedScheduleInfo: 0,
			monthOptions: [],
			frequencyMap: {
				[RRule.YEARLY]: 'YEARLY',
				[RRule.MONTHLY]: 'MONTHLY',
				[RRule.WEEKLY]: 'WEEKLY',
				[RRule.DAILY]: 'DAILY',
				[RRule.HOURLY]: 'HOURLY',
				[RRule.MINUTELY]: 'MINUTELY',
				[RRule.SECONDLY]: 'SECONDLY',
			},
			selectedScheduleToEdit: null,
			showDialog: false,
			formValid: false,
			isAllDayTask: false,
		};
	},
	computed: {
		...mapState({
			hospitals: (state) => state.hospitals.hospitals,
			userData: (state) => state.authentication.userData,
			item: (state) => state.item,
			authenticated: (state) => state.authentication.userData,
		}),
		freqOptions () {
			// | Frequency | RRule Constant | Numeric Value |
			// | --------- | -------------- | ------------- |
			// | YEARLY    | RRule.YEARLY   | 0             |
			// | MONTHLY   | RRule.MONTHLY  | 1             |
			// | WEEKLY    | RRule.WEEKLY   | 2             |
			// | DAILY     | RRule.DAILY    | 3             |
			// | HOURLY    | RRule.HOURLY   | 4             |
			// | MINUTELY  | RRule.MINUTELY | 5             |
			// | SECONDLY  | RRule.SECONDLY | 6             |
			return [
				{ text: this.$t('daily'), value: RRule.DAILY, key: 'DAILY' },
				{ text: this.$t('weekly'), value: RRule.WEEKLY, key: 'WEEKLY' },
				{ text: this.$t('monthly'), value: RRule.MONTHLY, key: 'MONTHLY' },
				{ text: this.$t('yearly'), value: RRule.YEARLY, key: 'YEARLY' },
			];
		},
		monthDayOptions () {
			return [
				...Array.from({ length: 31 }, (_, i) => ({ text: `${i + 1}`, value: i + 1 })),
				{ text: this.$t('last-day-of-the-month'), value: -1 },
			];
		},
		weekdayOptions () {
			const weekdayOptions = [
				{ text: this.$t('monday'), value: RRule.MO },
				{ text: this.$t('tuesday'), value: RRule.TU },
				{ text: this.$t('wednesday'), value: RRule.WE },
				{ text: this.$t('thursday'), value: RRule.TH },
				{ text: this.$t('friday'), value: RRule.FR },
				{ text: this.$t('saturday'), value: RRule.SA },
				{ text: this.$t('sunday'), value: RRule.SU },
			];
			return weekdayOptions;
		},
		headersHospitals () {
			return [{ text: this.$t('location-name'), value: 'hospitalName' }];
		},
		headersOwnersAndStaff () {
			return [{ text: this.$t('coordinator'), value: 'firstName' }];
		},
		canSaveRRule () {
			// 🧩 1. If the main schedule object doesn't exist, saving is not possible.
			if (!this.selectedScheduleToEdit) return false;
			// ✅ 3. Perform overall validation for the main schedule configuration
			const checkSchedule =
				this.selectedScheduleToEdit.scheduleLocations.length > 0 && // at least one location selected
				this.selectedScheduleToEdit.scheduleExecutors.length > 0 && // at least one executor assigned
				this.selectedScheduleToEdit.scheduleRuleName.length > 0; // schedule name is not empty

			// ✅ 4. Validate all individual Tasks/Occurrences in the schedule
			const checkTasksOccurrences =
				this.selectedScheduleToEdit.scheduleOccurrences && // occurrences exist
				this.selectedScheduleToEdit.scheduleOccurrences.length > 0 && // not empty
				this.selectedScheduleToEdit.scheduleOccurrences.reduce((acc, curr) => {
					// ensure that every occurrence:
					return (
						acc &&
						curr.occurrenceName.length > 0 && // has a name
						curr?.rruleString?.length > 0 && // has a recurrence rule string
						curr.occurrenceDuration > 0 // has a valid duration
					);
				}, true);
			// ✅ 5. Return true only if both the main schedule and all occurrences are valid.
			return checkSchedule && checkTasksOccurrences;
		},
	},
	async mounted () {
		this.monthOptions = this.months();
		await this.getHospitals();
		await this.getMedicalStaffAbleToDoBillingForOrg();
		this.initializeEditSchedule();

		this.currentMonth = new Date(this.currentDate).getMonth() + 1;
		this.currentYear = new Date(this.currentDate).getFullYear();
		this.loading = false;
	},
	methods: {
		getMonthName,
		months,
		getHospitals,
		monthName,
		...mapActions({
			updateSchedule: 'schedules/updateSchedule',
		}),
		async initializeEditSchedule () {
			this.selectedScheduleToEdit = {
				...this.item,
				scheduleLocations: this.hospitals.filter((h) =>
					this.item.scheduleLocations.map((i) => i.taskLocationId).includes(h.id),
				),
				scheduleExecutors: this.medicalStaffAbleToDoBillingForOrg.filter((h) =>
					this.item.scheduleExecutors.map((i) => i.taskExecutorUserId).includes(h.id),
				),
				scheduleOccurrences: this.item.scheduleOccurrences.map((o) => {
					const normalized = {
						...o,
						frequency: this.freqOptions.find((f) => f.key === o.frequency).value,
					};
					try {
						const { defaultTime, byHour, byMinute } = this.parseDefaultTimeFromRRule(o.rruleString);
						normalized.defaultTime = defaultTime || '';
						normalized.byHour = o.byHour && o.byHour.length ? o.byHour.slice() : byHour || [];
						normalized.byMinute = o.byMinute && o.byMinute.length ? o.byMinute.slice() : byMinute || [];
						// if exactly one hour and one minute, show it as defaultTime and clear arrays
						if (normalized.byHour.length === 1 && normalized.byMinute.length === 1) {
							const hh = normalized.byHour[0];
							const mm = normalized.byMinute[0];
							normalized.defaultTime = `${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}`;
							normalized.byHour = [];
							normalized.byMinute = [];
						}
						// default to midnight if no time info
						if (!normalized.byHour.length && !normalized.byMinute.length && !normalized.defaultTime) {
							normalized.defaultTime = '00:00';
						}
					} catch (e) {
						normalized.defaultTime = '00:00';
						normalized.byHour = o.byHour || [];
						normalized.byMinute = o.byMinute || [];
					}
					return normalized;
				}),
			};
		},
		deleteTaskOccurrence (indexSchedule) {
			this.selectedScheduleToEdit.scheduleOccurrences = this.selectedScheduleToEdit.scheduleOccurrences.filter(
				(t, index) => index !== indexSchedule,
			);
		},
		async addNewRRule () {
			// Add an empty rrule in the array
			const rruleToAdd = JSON.parse(JSON.stringify(this.rruleNew));
			this.selectedScheduleToEdit.scheduleOccurrences = [
				...this.selectedScheduleToEdit.scheduleOccurrences,
				rruleToAdd,
			];
			for (const tasksOccurrence of this.selectedScheduleToEdit.scheduleOccurrences) {
				tasksOccurrence.occurrenceStartAt = this.selectedScheduleToEdit.scheduleStartDate;
				if (this.selectedScheduleToEdit.scheduleEndDate !== null) {
					tasksOccurrence.occurrenceEndAt = this.selectedScheduleToEdit.scheduleEndDate;
				}
			}
		},
		async getMedicalStaffAbleToDoBillingForOrg () {
			await this.$store.dispatch('hospitals/getMedicalStaffAbleToDoBillingForOrg').then((res) => {
				this.medicalStaffAbleToDoBillingForOrg = res.data;
			});
		},
		onFileSelected (file) {},
		onFileUploaded (response) {
			showSuccessAlert(this.$t('successfully-created-organization'));
		},
		onFileError (err) {
			showErrorAlert(`${this.$t('error')}: ${err}`);
		},
		getFrequencyKeyByValue (value) {
			return this.frequencyMap[value] || null;
		},
		viewDate ({ date }) {
			this.calendarView = 'day';
			this.currentDate = date;
		},
		async saveRRule () {
			if (!this.$refs.scheduleForm.validate()) {
				return;
			}
			const ruleStrings = this.selectedScheduleToEdit.scheduleOccurrences.map((t) => t.rruleString);
			const rruleSet = new RRuleSet();
			// Add all rules
			ruleStrings.forEach((str) => {
				const fixedRRule = this.normalizeRRuleToLocal(str);
				const rule = RRule.fromString(fixedRRule);
				rruleSet.rrule(rule);
			});
			const combinedString = rruleSet.toString();

			this.selectedScheduleToEdit.scheduleRule = combinedString;

			const scheduleOccurrencesCreateDtoFormatBeforeSave = this.selectedScheduleToEdit.scheduleOccurrences.map((s) => {
				// exclude transient fields from the payload
				const { interval, count, defaultTime, ...occ } = s;

				// compute byHour/byMinute to send to backend: prefer explicit arrays, otherwise parse defaultTime
				let byHour = s.byHour && s.byHour.length ? s.byHour : [];
				let byMinute = s.byMinute && s.byMinute.length ? s.byMinute : [];
				// If hours are set but minutes are not, default minutes to 0 for saving
				if (byHour.length && !byMinute.length) {
					byMinute = [0];
				}
				if (!byHour.length && !byMinute.length && s.defaultTime) {
					const parts = s.defaultTime.split(':').map((p) => Number(p));
					const h = Number.isFinite(parts[0]) ? parts[0] : null;
					const m = parts.length > 1 && Number.isFinite(parts[1]) ? parts[1] : 0;
					if (h !== null && !Number.isNaN(h)) byHour = [h];
					if (!Number.isNaN(m)) byMinute = [m];
				}

				return {
					...occ,
					byWeekDay:
						s.byWeekDay && s.byWeekDay.length
							? s.byWeekDay.map((w) => (w && w.weekday !== undefined ? w.weekday : w))
							: [],
					startDayOfWeek:
						s.startDayOfWeek && s.startDayOfWeek.weekday !== undefined ? s.startDayOfWeek.weekday : s.startDayOfWeek,
					frequency: this.getFrequencyKeyByValue(s.frequency),
					...(interval !== '' && Number(interval) > 1 ? { interval: Number(interval) } : {}),
					...(count !== '' && Number(count) > 1 ? { count: Number(count) } : {}),
					...(byHour.length ? { byHour } : {}),
					...(byMinute.length ? { byMinute } : {}),
				};
			});
			const body = {
				id: this.selectedScheduleToEdit.id,
				scheduleRule: this.selectedScheduleToEdit.scheduleRule, // 'string',
				scheduleRuleName: this.selectedScheduleToEdit.scheduleRuleName, // 'string',
				scheduleRuleDescription: this.selectedScheduleToEdit.scheduleRuleDescription, // 'string',
				scheduleEndDate: this.selectedScheduleToEdit.scheduleEndDate,
				scheduleLocations: this.selectedScheduleToEdit.scheduleLocations.map((l) => l.id),
				scheduleExecutors: this.selectedScheduleToEdit.scheduleExecutors.map((l) => l.id),
				scheduleOccurrencesForEditDto: scheduleOccurrencesCreateDtoFormatBeforeSave,
			};

			this.updateSchedule(body).then((res) => {
				if (res.data.resFlag) {
					const selectedHospitalAssignedId = JSON.parse(localStorage.getItem('selectedHospitalAssignedId'));
					this.$store.dispatch('schedules/getSchedulesByLocationId', selectedHospitalAssignedId);
					// Reset fields
					this.closeDrawer();
				} else {
					showErrorAlert(res.data.msg);
				}
			});
		},

		async generateEventsFromRRuleStrings (rruleList = this.selectedScheduleToEdit.scheduleOccurrences || []) {
			this.calendarEvents = [];
			rruleList.forEach((rruleItem) => {
				const floatingRRule = rruleItem.rruleString.replace(/Z/g, '');
				const occurs = RRule.fromString(floatingRRule.replace(' ', '\n'), { tzid: null }).all();
				occurs.forEach((d) => {
					const date = new Date(d.getTime() + d.getTimezoneOffset() * 60000);
					this.calendarEvents.push({
						name: rruleItem.occurrenceName || '',
						description: rruleItem.occurrenceDescription || '',
						start: date,
						end: new Date(date.getTime() + Number(rruleItem.occurrenceDuration) * 60 * 1000),
						timed: true,
						color: rruleItem.color,
						textColor: rruleItem.textColor,
					});
				});
			});
		},
		// Calendar methods
		goPrev () {
			const base = new Date(this.currentDate);
			if (this.calendarView === 'month') base.setMonth(base.getMonth() - 1);
			else if (this.calendarView === 'week') base.setDate(base.getDate() - 7);
			else base.setDate(base.getDate() - 1);
			this.currentDate = base.toISOString().substring(0, 10);
			this.currentMonth = new Date(base).getMonth() + 1;
			this.currentYear = new Date(base).getFullYear();
		},
		goNext () {
			const base = new Date(this.currentDate);
			if (this.calendarView === 'month') base.setMonth(base.getMonth() + 1);
			else if (this.calendarView === 'week') base.setDate(base.getDate() + 7);
			else base.setDate(base.getDate() + 1);
			this.currentDate = base.toISOString().substring(0, 10);
			this.currentMonth = new Date(base).getMonth() + 1;
			this.currentYear = new Date(base).getFullYear();
		},
		goToday () {
			const base = new Date();
			this.currentDate = base.toISOString().substr(0, 10);
			this.currentMonth = new Date(base).getMonth() + 1;
			this.currentYear = new Date(base).getFullYear();
			this.currentMonth = new Date(base).getMonth() + 1;
			this.currentYear = new Date(base).getFullYear();
		},
		normalizeRRuleToLocal (rruleString) {
			return rruleString.replace(/DTSTART:(\d{8})T(\d{6})Z/, (_, date, time) => {
				// Parse UTC time into a Date
				const utcDate = new Date(
					`${date.slice(0, 4)}-${date.slice(4, 6)}-${date.slice(6, 8)}T${time.slice(0, 2)}:${time.slice(
						2,
						4,
					)}:${time.slice(4, 6)}Z`,
				);

				// Convert to local time
				const localDate = new Date(utcDate.getTime() - utcDate.getTimezoneOffset() * 60000);

				// Return local version without "Z"
				const formatted =
					`DTSTART:${localDate.getFullYear()}${String(localDate.getMonth() + 1).padStart(2, '0')}${String(
						localDate.getDate(),
					).padStart(2, '0')}T` +
					`${String(localDate.getHours()).padStart(2, '0')}${String(localDate.getMinutes()).padStart(2, '0')}${String(
						localDate.getSeconds(),
					).padStart(2, '0')}`;

				return formatted;
			});
		},
		parseDefaultTimeFromRRule (rruleString) {
			// Returns { defaultTime: 'HH:MM' | '', byHour: [], byMinute: [] }
			if (!rruleString) return { defaultTime: '', byHour: [], byMinute: [] };
			// Try to extract DTSTART first
			const dtMatch = rruleString.match(/DTSTART:(\d{8})T(\d{6})Z?/);
			if (dtMatch) {
				try {
					const date = dtMatch[1];
					const time = dtMatch[2];
					const utcDate = new Date(
						`${date.slice(0, 4)}-${date.slice(4, 6)}-${date.slice(6, 8)}T${time.slice(0, 2)}:${time.slice(
							2,
							4,
						)}:${time.slice(4, 6)}Z`,
					);
					const local = new Date(utcDate.getTime() - utcDate.getTimezoneOffset() * 60000);
					const hh = local.getHours();
					const mm = local.getMinutes();
					return {
						defaultTime: `${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}`,
						byHour: [hh],
						byMinute: [mm],
					};
				} catch (e) {
					// fallthrough to parsing via occurrences
				}
			}
			// Otherwise, try to compute from a sample of occurrences
			try {
				const floating = rruleString.replace(/Z/g, '');
				const rule = RRule.fromString(floating.replace(' ', '\n'), { tzid: null });
				const occ = rule.all().slice(0, 50);
				const hours = new Set();
				const minutes = new Set();
				occ.forEach((d) => {
					const date = new Date(d.getTime() + d.getTimezoneOffset() * 60000);
					hours.add(date.getHours());
					minutes.add(date.getMinutes());
				});
				const byHour = Array.from(hours).sort((a, b) => a - b);
				const byMinute = Array.from(minutes).sort((a, b) => a - b);
				let defaultTime = '';
				if (byHour.length === 1 && byMinute.length === 1) {
					defaultTime = `${String(byHour[0]).padStart(2, '0')}:${String(byMinute[0]).padStart(2, '0')}`;
				}
				return { defaultTime, byHour, byMinute };
			} catch (e) {
				return { defaultTime: '', byHour: [], byMinute: [] };
			}
		},
		previewSchedule () {
			this.generateEventsFromRRuleStrings().then(() => {
				this.showDialog = true;
			});
		},

		closeDrawer () {
			this.initializeEditSchedule();
			this.$store.dispatch('schedules/getSchedulesByOwnerId', this.authenticated.id);
			this.$store.commit('SET_UPDATE_DETAILS_DRAWER', false);
		},
		hasOccurrenceAnyTask (occurrenceId) {
			const hasTasks = this.selectedScheduleToEdit.scheduleTasksDetail.find((t) => t.occurrenceId === occurrenceId);
			return !!hasTasks;
		},
		onIsAllDayTaskChange ($event, indexSchedule) {
			this.selectedScheduleToEdit.scheduleOccurrences[indexSchedule].occurrenceDuration = $event ? 1439 : 60;
			this.isAllDayTask = $event;
		},
	},
	watch: {
		'selectedScheduleToEdit.scheduleOccurrences': {
			handler (newVal) {
				if (!newVal) return;
				newVal.forEach((taskOccurrence) => {
					try {
						let byhour = taskOccurrence.byHour && taskOccurrence.byHour.length ? taskOccurrence.byHour : [];
						let byminute = taskOccurrence.byMinute && taskOccurrence.byMinute.length ? taskOccurrence.byMinute : [];
						// If hours are provided but minutes are not, default minutes to 0 for preview/building RRule
						if (byhour.length && !byminute.length) {
							byminute = [0];
						}
						if (!byhour.length && !byminute.length && taskOccurrence.defaultTime) {
							const parts = taskOccurrence.defaultTime.split(':').map((p) => Number(p));
							const h = Number.isFinite(parts[0]) ? parts[0] : null;
							const m = parts.length > 1 && Number.isFinite(parts[1]) ? parts[1] : 0;
							if (h !== null && !Number.isNaN(h)) byhour = [h];
							if (!Number.isNaN(m)) byminute = [m];
						}
						// Build the RRule configuration from your object
						const rruleConfig = {
							dtstart: new Date(taskOccurrence.occurrenceStartAt),
							...(taskOccurrence.occurrenceEndAt && { until: new Date(taskOccurrence.occurrenceEndAt) }),
							...(taskOccurrence.frequency && { freq: taskOccurrence.frequency }),
							...(taskOccurrence.interval && { interval: taskOccurrence.interval }),
							...(taskOccurrence.count && { count: taskOccurrence.count }),
							...(taskOccurrence.byWeekDay?.length && { byweekday: taskOccurrence.byWeekDay }),
							...(taskOccurrence.byMonth?.length && { bymonth: taskOccurrence.byMonth }),
							...(taskOccurrence.byMonthDay?.length && { bymonthday: taskOccurrence.byMonthDay }),
							...(taskOccurrence.byYearDay?.length && { byyearday: taskOccurrence.byYearDay }),
							...(taskOccurrence.byWeekNumber?.length && { byweekno: taskOccurrence.byWeekNumber }),
							...(byhour.length && { byhour }),
							...(byminute.length && { byminute }),
							wkst: taskOccurrence.startDayOfWeek?.weekday ?? RRule.MO,
						};

						// Create the rule
						const rrule = new RRule(rruleConfig);
						// Assign the generated rule string
						taskOccurrence.rruleString = rrule.toString();
					} catch (err) {
						taskOccurrence.rruleString = '';
					}
				});
			},
			deep: true,
			immediate: false,
		},
		selectedScheduleToEdit (newVal) {
			if (newVal === null) {
				this.$store.dispatch('schedules/getSchedulesByOwnerId', this.authenticated.id);
			}
		},
		async '$store.state.updateDrawer' (newVal) {
			if (newVal) {
				this.loading = true;
				this.monthOptions = this.months();
				await this.getHospitals();
				// await this.initializeNewScheduleCreation();
				await this.getMedicalStaffAbleToDoBillingForOrg();

				this.selectedScheduleToEdit = {
					...this.item,
					scheduleLocations: this.hospitals.filter((h) =>
						this.item.scheduleLocations.map((i) => i.taskLocationId).includes(h.id),
					),
					scheduleExecutors: this.medicalStaffAbleToDoBillingForOrg.filter((h) =>
						this.item.scheduleExecutors.map((i) => i.taskExecutorUserId).includes(h.id),
					),
					scheduleOccurrences: this.item.scheduleOccurrences.map((o) => {
						const normalized = {
							...o,
							frequency: this.freqOptions.find((f) => f.key === o.frequency).value,
						};
						try {
							const { defaultTime, byHour, byMinute } = this.parseDefaultTimeFromRRule(o.rruleString);
							normalized.defaultTime = defaultTime || '';
							normalized.byHour = o.byHour && o.byHour.length ? o.byHour.slice() : byHour || [];
							normalized.byMinute = o.byMinute && o.byMinute.length ? o.byMinute.slice() : byMinute || [];
							if (normalized.byHour.length === 1 && normalized.byMinute.length === 1) {
								const hh = normalized.byHour[0];
								const mm = normalized.byMinute[0];
								normalized.defaultTime = `${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}`;
								normalized.byHour = [];
								normalized.byMinute = [];
							}
							if (!normalized.byHour.length && !normalized.byMinute.length && !normalized.defaultTime) {
								normalized.defaultTime = '00:00';
							}
						} catch (e) {
							normalized.defaultTime = '00:00';
							normalized.byHour = o.byHour || [];
							normalized.byMinute = o.byMinute || [];
						}
						return normalized;
					}),
				};
				this.loading = false;
			}
		},
	},
};
</script>
<style scoped>
.v-dialog__content {
	z-index: 2001 !important;
}
.primary2Bg {
	background-color: #a41717 !important;
}
.primaryBg {
	background-color: #ff585f !important;
}
</style>
